/**
 * PageHeader — 페이지 공통 헤더.
 *
 * 구조: 상단 영역 전체(제목줄 + 옵션 메인탭)를 하나의 sticky 카드(.page-hd--stack)로 묶는다.
 *       제목줄(.page-hd-row) 과 메인탭(.main-tabs) 이 같은 박스 안 자연 흐름이므로,
 *       desc 가 접혀 제목줄 높이가 줄어도 탭이 자동으로 따라 올라온다(별도 위치 측정 불필요).
 *       그림자·라운드·간격(--hd-gap)은 이 박스 하나에만 걸려 탭/서브 유무와 무관하게 일관된다.
 *   <header class="page-hd page-hd--stack">
 *     <div class="page-hd-row">
 *       <div class="page-hd-titleline"><h1>{title}</h1>{actions}</div>
 *       <div class="page-hd-desc"><p>{desc}</p></div>
 *     </div>
 *     <div class="main-tabs" role="tablist">{tabs}</div>   ← tabs 가 있을 때만
 *   </header>
 *
 * actions: caller가 <div className="page-actions">…</div> 를 통째로 넘김(제목과 같은 줄).
 * tabs: 메인 탭 버튼들(<button class="main-tab">…). PageHeader 가 .main-tabs 래퍼로 감싼다.
 *       embedded(.card 안) 같이 PageHeader 를 안 쓰는 곳은 caller 가 직접 .main-tabs 를 렌더.
 * extra: 제목줄(+탭) 아래에 sticky 카드 안으로 함께 들어갈 임의 콘텐츠(예: Quote 의 KPI 스트립).
 *        caller 가 자체 클래스(div)를 통째로 넘기며, 카드 하단부로 헤더와 함께 플로팅된다.
 * className: 페이지별 추가 클래스 (page-settings-hd 등).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

interface Props {
  title: ReactNode;
  desc?: ReactNode;
  actions?: ReactNode;
  tabs?: ReactNode;
  tabsLabel?: string;
  extra?: ReactNode;
  className?: string;
}

export function PageHeader({ title, desc, actions, tabs, tabsLabel, extra, className }: Props) {
  const headerRef = useRef<HTMLElement>(null);
  const titleLineRef = useRef<HTMLDivElement>(null);
  const descRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);

  /**
   * MO·fold 에서 탭 줄은 한 줄 가로 스크롤이다. 활성 탭이 스크롤 밖(예: 6번째 탭)에 있으면
   * 진입했을 때 어느 탭인지 보이지 않으므로 화면 안으로 당겨 온다.
   */
  useEffect(() => {
    const wrap = tabsRef.current;
    if (!wrap || wrap.scrollWidth <= wrap.clientWidth) return;
    const active = wrap.querySelector<HTMLElement>(".main-tab.is-active");
    if (!active) return;
    const left = active.offsetLeft - (wrap.clientWidth - active.offsetWidth) / 2;
    wrap.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [tabs]);
  const [scrolled, setScrolled] = useState(false);

  // 스크롤 구독: is-scrolled(카드 분리) + 진행값 2개를 .page 에 셋(리렌더 없이 CSS 변수만).
  //   --hd-resize: desc 높이 수축 진행(8→28px : 0→1)  — 먼저
  //   --hd-fade  : desc 투명도 소멸 진행(24→40px : 0→1) — 늦게·짧게
  useEffect(() => {
    const scroller = headerRef.current?.closest(".content") as HTMLElement | null;
    if (!scroller) return;
    const page = headerRef.current?.closest(".page") as HTMLElement | null;
    const onScroll = () => {
      const y = scroller.scrollTop;
      const stuck = y > 4;
      if (page) {
        // 축약(is-scrolled) 스페이서 스위치 — 클래스(React 리렌더)보다 먼저 세팅해
        // 헤더가 줄기 전에 .page::after 가 Δ 만큼 자라도록(문서 총 높이가 한순간도 줄지 않게).
        page.style.setProperty("--hd-stuck", stuck ? "1" : "0");
      }
      setScrolled(stuck);
      if (page) {
        // 디졸브 먼저(opacity 100→0, 8~24px), 그 다음 높이 수축(24~40px) → 투명도 변화가 또렷이 보임
        page.style.setProperty("--hd-fade", String(clamp01((y - 8) / 16)));
        page.style.setProperty("--hd-resize", String(clamp01((y - 24) / 16)));
      }
    };
    onScroll();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  // is-scrolled 축약으로 줄어드는 헤더 높이(Δ)를 미리 재서 --hd-stuck-delta 로 둔다.
  //   페이지가 뷰포트보다 조금만 길 때, 축약으로 문서 총 높이가 줄면 브라우저가 scrollTop 을 clamp
  //   → y ≤ 4 → 다시 펼침 → y > 4 → 다시 축약 … 진동(출하조회 KPI 카드 떨림). .page::after 스페이서가
  //   축약 시 Δ 만큼 즉시 자라 총 높이를 고정하면 루프가 성립하지 않는다.
  //   측정은 실물이 아닌 숨김 clone 두 개(펼침/축약)로 — transition 중인 실물을 건드리지 않는다.
  useEffect(() => {
    const el = headerRef.current;
    const page = el?.closest(".page") as HTMLElement | null;
    if (!el || !page) return;
    const measure = () => {
      const width = el.getBoundingClientRect().width;
      if (!width) return;
      const make = (stuck: boolean) => {
        const c = el.cloneNode(true) as HTMLElement;
        c.classList.add("is-measuring");
        c.classList.toggle("is-scrolled", stuck);
        c.removeAttribute("id");
        c.setAttribute("aria-hidden", "true");
        c.style.width = `${width}px`;
        return c;
      };
      const rest = make(false);
      const stuck = make(true);
      el.after(rest, stuck);
      const delta = Math.max(0, rest.offsetHeight - stuck.offsetHeight);
      rest.remove();
      stuck.remove();
      page.style.setProperty("--hd-stuck-delta", `${delta}px`);
    };
    let timer = 0;
    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, 200);
    };
    measure();
    const ro = new ResizeObserver(schedule); // 폭 변경·내용 변경 시 재측정(디바운스)
    ro.observe(el);
    return () => {
      window.clearTimeout(timer);
      ro.disconnect();
    };
  }, [desc, tabs, extra]);

  // desc 실제 높이 → --hd-desc-h (max-height 비례 수축 기준). 안쪽 <p>의 자연 높이로 측정.
  useEffect(() => {
    const inner = descRef.current?.firstElementChild as HTMLElement | null;
    const page = headerRef.current?.closest(".page") as HTMLElement | null;
    if (!inner || !page) return;
    const sync = () => page.style.setProperty("--hd-desc-h", `${inner.offsetHeight}px`);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [desc]);

  // 제목줄 아래에 탭/extra 등 하단 콘텐츠가 있으면 has-foot — 카드가 "제목만"이 아님을 CSS 에 알림
  // (스크롤 시 제목 수직중앙 정렬은 has-foot 없는 순수 헤더에만 적용).
  const hasFoot = (tabs != null && tabs !== false) || (extra != null && extra !== false);

  return (
    <header
      ref={headerRef}
      className={`page-hd page-hd--stack${hasFoot ? " has-foot" : ""}${scrolled ? " is-scrolled" : ""}${className ? ` ${className}` : ""}`}
    >
      <div className="page-hd-row">
        <div className="page-hd-titleline" ref={titleLineRef}>
          <h1>{title}</h1>
          {actions}
        </div>
        {desc != null && desc !== false && (
          <div className="page-hd-desc" ref={descRef}>
            <p>{desc}</p>
          </div>
        )}
      </div>
      {tabs != null && tabs !== false && (
        <div className="main-tabs" role="tablist" aria-label={tabsLabel} ref={tabsRef}>
          {tabs}
        </div>
      )}
      {extra}
    </header>
  );
}
